import { useState, useEffect, useRef, } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';

import { siteboardAction, setAlert } from '../actions/defaultActions';
import { 
  SET_ADMIN_DIALOGUE_OPEN, SEND_EMAIL, PROMO_CODE_CREATE, PROMO_CODE_FETCH_ALL, SET_DELETE_PROMO_CODE_DIALOGUE_OPEN, PROMO_CODE_SET_DISABLED,
  SET_NOTE_PROMO_CODE_DIALOGUE_OPEN, SET_EXPIRATION_PROMO_CODE_DIALOGUE_OPEN, ShareScheduleCode,
} from '../actions/types';
import { objectValuesToList, copyToClipboard, } from '../Util';

// components
import PopupWindow from './PopupWindow';
import PushButton from './PushButton';
import SortableTable from './SortableTable';
import CheckmarkToggle from './CheckmarkToggle';
import TabSelector from './TabSelector';

// icons
import Logo from '../SiteBoard_Logo';
import EmailIcon from '../icons/emailIcon';
import SendIcon from '../icons/sendIcon';
import AddIcon from '../icons/addIcon';
import TagIcon from '../icons/tagIcon';
import TrashIcon from '../icons/trashIcon';
import ClockIcon from '../icons/clockIcon';
import TimeIcon from '../icons/timeIcon';
import NotepadIcon from '../icons/notepadIcon';
import CircleXIcon from '../icons/circleXIcon';
import LinkIcon from '../icons/linkIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';

const testScrollPromoCodes = false;

const PAGE_PROMO_CODES = 'PAGE_PROMO_CODES';
const PAGE_EMAIL = 'PAGE_EMAIL';

const AdminDialogue = ({ _siteboardEmail, _promoCodes, siteboardAction, setAlert }) => {
  const [currentPage, setCurrentPage] = useState(PAGE_PROMO_CODES);
  const [promoCodesLoading, setPromoCodesLoading] = useState(true);
  const [showTimeAndDate, setShowTimeAndDate] = useState(false);
  const [promoLinkCopiedIndex, setPromoLinkCopiedIndex] = useState(false); // set the table index of the promo code that as just copied to the clipboard
  const [promoCodeCopied, setPromoCodeCopied] = useState(null); // promo code that was just copied to the clipboard, used to highlight the code's table row with a copy confirm message
  const promoCodeCopiedConfirmTimeout = useRef(null);

  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailTitle, setEmailTitle] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const currentTime = new Date().getTime();
  const sortedPromoCodes = objectValuesToList(_promoCodes);
  if(testScrollPromoCodes) {
    for(let i = 0; i < 15; i++) {
      sortedPromoCodes.push(...objectValuesToList(_promoCodes));
    }
  }
  const closeDialogue = () => {
    siteboardAction(SET_ADMIN_DIALOGUE_OPEN, { isOpen: false });
  };

  // Fetch promo codes from the server. If we already have them this will do nothing
  useEffect(() => {
    siteboardAction(PROMO_CODE_FETCH_ALL, {}, () => {
      setPromoCodesLoading(false);
    });
  }, [siteboardAction]);

  useEffect(() => {
    if(!ShareScheduleCode.isValid(promoCodeCopied)) return;
    if(promoCodeCopiedConfirmTimeout.current !== null) {
      clearTimeout(promoCodeCopiedConfirmTimeout.current);
    }
    promoCodeCopiedConfirmTimeout.current = setTimeout(() => {
      setPromoCodeCopied(null);
    }, 5000);
  }, [promoCodeCopied]);

  const onSendEmail = () => {
    try {
      siteboardAction(SEND_EMAIL, {
        emailRecipient: emailRecipient,
        emailTitle: emailTitle,
        emailBody: emailBody,
      }, () => {
        closeDialogue();
      });
    } catch(error) {
      setAlert(''+error.message);
    }
  };

  const getPromoCodeStatusJSX = (promoCode) => {
    if(promoCode.redeemedTimestamp > 0) return (<div className='inline-block rounded-full py-0.5 px-2 bg-[#349951]'>Redeemed</div>);
    if(currentTime < promoCode.expiresAtTimestamp) return (<div className='inline-block rounded-full py-0.5 px-2 bg-blue-600'>Active</div>);
    return (<div className='inline-block rounded-full py-0.5 px-2 bg-red-800'>Expired</div>);
  };

  const isPromoCodeActive = (promoCode) => {
    return promoCode.redeemedTimestamp === 0 && currentTime < promoCode.expiresAtTimestamp;
  };

  const isPromoCodeRedeemed = (promoCode) => {
    return promoCode.redeemedTimestamp > 0 && promoCode.redeemedEmail !== '';
  };

  const getDateStringForTimestamp = (value) => {
    if(typeof value !== 'number' || value === 0) return '-';
    return showTimeAndDate ? format(new Date(value), 'MM/dd/yy h:mm aa') : format(new Date(value), 'MM/dd/yy')
  };

  const getPromoCodesTableJSX = () => {
    const columns = [
      { key: "code", label: "code", width:'8.45%', render: (value, row) => ShareScheduleCode.truncate(value)},
      { key: "promoType", label: "plan", width:'6.45%', render: (value) => value, },
      { key: null, label: "status", width:'6.45%', render: (value, row) => getPromoCodeStatusJSX(row), },
      { key: "createdTimestamp", label: "creation", width:'8%', render: (value) => getDateStringForTimestamp(value), },
      { key: "creatorEmail", label: "creator", width:'20.25%', render: (value) => value, },
      { key: "redeemedEmail", label: "redeemer", width:'20.25%', render: (value) => (value && value.length > 0 ? value : '-'), },
      { key: "redeemedTimestamp", label: "redeemed", width:'8%', render: (value) => getDateStringForTimestamp(value), },
      { key: "expiresAtTimestamp", label: "expires", width:'8%', render: (value, row) => isPromoCodeRedeemed(row) ? '-' : getDateStringForTimestamp(value), },
      { key: "note", label: "note", width:'12.9%', render: (value) => value, },
      { key: null, label: "actions", width:'1.2%', render: (value, row) => <div className='flex justify-center'><MoreOptionsButton items={[
          ...(!isPromoCodeRedeemed(row) ? [{
            label: row.code === promoCodeCopied  ? <div className='animate-pop'>Link Copied</div> : <div>Copy Link</div>,
            icon: row.code === promoCodeCopied ? <CircleCheckIcon size='1em' color='#00bc7d' /> : <LinkIcon size='1em' color={'#fff'} />,
            action: () => {
              copyToClipboard([ 
                process.env.REACT_APP_LIVE_MODE === 'true' ? process.env.REACT_APP_WEBSITE_DOMAIN : 'http://localhost:3000',
                `promoCode=${encodeURIComponent(row.code)}`,
              ].join('/?'));
              setPromoCodeCopied(row.code);
            },
          }] : []),
          {
            label: "Set Note",
            icon: <NotepadIcon size='1em' color={'#fff'} />,
            action: () => {
              siteboardAction(SET_NOTE_PROMO_CODE_DIALOGUE_OPEN, { isOpen: true, promoCodeId: row.id });
            },
          },
          ...(!isPromoCodeRedeemed(row) ? [{
            label: "Set Expiration",
            icon: <ClockIcon size='1em' color={'#fff'} />,
            action: () => {
              siteboardAction(SET_EXPIRATION_PROMO_CODE_DIALOGUE_OPEN, { isOpen: true, promoCodeId: row.id });
            },
          }] : []),
          //...(isPromoCodeActive(row) ? [{
          //  label: "Disable",
          //  icon: <CircleXIcon size='1em' color={'#fff'} />,
          //  action: () => {
          //    siteboardAction(PROMO_CODE_SET_DISABLED, { code: row.code });
          //  },
          //}] : []),
          ...(!isPromoCodeRedeemed(row) ? [{
            label: "Delete",
            icon: <TrashIcon size='1em' color={'#fff'} />,
            action: () => {
              siteboardAction(SET_DELETE_PROMO_CODE_DIALOGUE_OPEN, { isOpen: true, promoCodeId: row.id });
            },
          }] : []),
        ]} /></div>, 
      },
    ];
    return (
      <SortableTable 
        columns={columns}
        data={sortedPromoCodes}
        highlight={{
          key: "code",
          value: promoCodeCopied,
          color: "#00c950",
        }}
      />
    );
  };

  return (
    <PopupWindow title={`Admin Tools`} closeDialogue={closeDialogue} bgStyle='bg-neutral-800' isFullHeight={true}>
      <div className='flex flex-col text-white max-w-screen-2xl grow'>
        <div className='w-[90vw] max-w-full' />
        <TabSelector 
          tabs={[
            {
              label: "Promo Codes",
              icon: <TagIcon size='1em' color='white' />,
            },
            {
              label: "Email",
              icon: <EmailIcon size='1em' color='white' />,
            },
          ]} 
          initialTab={0} 
          onChange={(tabIndex) => {  
            setCurrentPage(tabIndex === 0 ? PAGE_PROMO_CODES : PAGE_EMAIL);
          }}
        />
        {currentPage === PAGE_PROMO_CODES &&
          <div className='flex flex-col gap-y-5 mt-5 animate-left-fade grow'>
            <div className='flex justify-between items-end'>
              <div className=''>
                <CheckmarkToggle label='Show Time + Date' initiallyChecked={showTimeAndDate} onChange={(value) => { setShowTimeAndDate(value); }} />
              </div>
              <PushButton 
                isInline={true}
                textColor='white'
                bgColor='#471cff'
                hoverBgColor="#603aff"
                onClick={()=>{ siteboardAction(PROMO_CODE_CREATE, {}); }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <AddIcon size='1em' color='white' />
                  <div>Generate Promo Code</div>
                </div>
              </PushButton>
            </div>
            {getPromoCodesTableJSX()}
            {promoCodesLoading &&
              <div className={`flex flex-col justify-center gap-y-1 p-6`}>
                <div className='flex rounded-lg py-6 px-10 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
                  <div className='animate-[spin_1s_linear_infinite]'>
                    <Logo size={48} />
                  </div>
                  <p className='select-none'>Loading Promo Codes</p>
                </div>
              </div>
            }
          </div>
        }
        {currentPage === PAGE_EMAIL &&
          <div className='flex flex-col gap-y-5 mt-5 animate-right-fade grow'>
            <div>
              <label className='block text-sm font-medium mb-1'>From</label>
              <input 
                className="project-detail-input"
                type='text' 
                disabled={true}
                value={_siteboardEmail}
                onChange={(event) => { setEmailRecipient(event.target.value); }}
              />
            </div>
            <div>
              <label className='block text-sm font-medium mb-1'>To</label>
              <input 
                className="project-detail-input"
                type='text' 
                value={emailRecipient}
                onChange={(event) => { setEmailRecipient(event.target.value); }}
              />
            </div>
            <div>
              <label className='block text-sm font-medium mb-1'>Subject / Title</label>
              <input 
                className="project-detail-input"
                type='text' 
                value={emailTitle}
                onChange={(event) => { setEmailTitle(event.target.value); }}
              />
            </div>
            <div className='flex flex-col grow'>
              <label className='block text-sm font-medium mb-1'>Body</label>
              <textarea 
                className={'project-detail-input leading-[1.4em] p-3 resize-none font-medium grow'}
                value={emailBody}
                onChange={(event) => { setEmailBody(event.target.value); }}
              >
              </textarea>
            </div>
            <PushButton 
              textColor='white'
              bgColor='#471cff'
              hoverBgColor="#603aff"
              onClick={onSendEmail}
            >
              <div className='flex items-center gap-x-[6px]'>
                <SendIcon size='1.2em'/>
                <div>Send Email</div>
              </div>
            </PushButton>
          </div>
        }
      </div>
    </PopupWindow>
 );
};

/**
 * Popup menu containing a list of actions.
 *
 * @param {Object} props
 * @param {PopupMenuItem[]} props.items
 * Menu items to display.
 *
 * @param {boolean} [props.visible=true]
 * Whether the menu is visible.
 *
 * @param {boolean} [props.openAbove=false]
 * Whether the menu should appear above its trigger.
 *
 * @returns {JSX.Element|null}
 */
export function PopupMenu({
  items,
  visible = true,
  openAbove = false,
}) {
  if (!visible) {
    return null;
  }

  return (
    <div
      className={[
        "absolute right-0 z-50",
        "min-w-48",
        "overflow-hidden",
        "rounded-lg",
        "border border-neutral-800",
        "bg-[#333]",
        "shadow-2xl",
        "ring-1 ring-black/40",
        openAbove
          ? "bottom-1/2 mb-3"
          : "top-1/2 mt-3",
      ].join(" ")}
    >
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          disabled={item.disabled}
          onClick={item.action}
          className="
            flex w-full items-center gap-3
            px-4 py-3
            text-left text-sm text-gray-200
            transition-colors duration-150
            hover:bg-[#444]
            hover:text-white
            disabled:cursor-not-allowed
            disabled:opacity-40
          "
        >
          {item.icon && (
            <span
              className="
                flex h-5 w-5 shrink-0
                items-center justify-center
                text-gray-400
                group-hover:text-gray-200
              "
            >
              {item.icon}
            </span>
          )}

          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * A "more options" button that reveals a popup menu when hovered.
 *
 * The menu automatically opens above the button when there is insufficient
 * space below it in the viewport.
 *
 * An invisible hover bridge is placed between the button and popup so the
 * menu does not close while the pointer is traveling between them.
 *
 * @param {Object} props
 * @param {PopupMenuItem[]} props.items
 * Menu items displayed in the popup.
 *
 * @returns {JSX.Element}
 */
export function MoreOptionsButton({ items }) {
  const [popupMenuVisible, setPopupMenuVisible] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!popupMenuVisible || !containerRef.current) {
      return;
    }

    const updateMenuPosition = () => {
      const rect = containerRef.current.getBoundingClientRect();

      // Approximate popup height. The actual popup can be taller, but this
      // gives us a reasonable position before rendering.
      const estimatedMenuHeight = Math.min(
        items.length * 46 + 8,
        400
      );

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setOpenAbove(
        spaceBelow < estimatedMenuHeight &&
          spaceAbove > spaceBelow
      );
    };

    updateMenuPosition();

    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener(
        "resize",
        updateMenuPosition
      );
      window.removeEventListener(
        "scroll",
        updateMenuPosition,
        true
      );
    };
  }, [popupMenuVisible, items.length]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={() => setPopupMenuVisible(true)}
      onMouseLeave={() => setPopupMenuVisible(false)}
    >
      <button
        type="button"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={popupMenuVisible}
        className="
          flex h-8 w-8
          items-center justify-center
          rounded
          text-lg font-semibold
          tracking-widest
          text-gray-400
          transition-colors duration-150
          hover:bg-[#3a3a3a]
          hover:text-gray-200
          focus:outline-none
          focus:ring-2
          focus:ring-blue-500
        "
      >
        <span
          aria-hidden="true"
          className="relative -top-1"
        >
          •••
        </span>
      </button>

      {popupMenuVisible && (
        <>
          {/* Invisible hover bridge between the button and popup. */}
          <div
            className={[
              "absolute right-0 w-[170px] z-40",
              openAbove
                ? "bottom-1/2 h-[50px]"
                : "top-1/2 h-[50px]",
            ].join(" ")}
            aria-hidden="true"
          />

          <div
            onClick={()=>{ 
              setPopupMenuVisible(false); 
            }}
          >
            <PopupMenu
              items={items}
              visible={popupMenuVisible}
              openAbove={openAbove}
            />
          </div>
        </>
      )}
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _siteboardEmail: state.localEphemeral.siteboardEmail,
  _promoCodes: state.serverSynced.promoCodes,
}};
export default connect(mapStateToProps, { siteboardAction, setAlert })(AdminDialogue);
